/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-preact';

/*----------------------------------
- APP
----------------------------------*/

import {
    CONTACT_CALLBACK_OPTIONS,
    CONTACT_DEFAULT_MESSAGE_BY_TOPIC,
    CONTACT_TOPIC_OPTIONS,
} from '@/client/catalogs/marketing/contact';
import Page from '@/client/pages/_layout/_page';
import { contactLeadTopics, type ContactLeadCallbackType, type ContactLeadPayload, type ContactLeadTopic } from '@/common/types/marketing/contactLead';
import useHeader from '@client/pages/useHeader';
import PublicHeader from '../PublicHeader';

/*----------------------------------
- TYPES
----------------------------------*/

type ContactFormData = {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    company: string;
    topic: ContactLeadTopic;
    callbackType: ContactLeadCallbackType;
    message: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const INITIAL_FORM_DATA: ContactFormData = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    topic: 'general',
    callbackType: 'email',
    message: '',
};

/*----------------------------------
- HELPERS
----------------------------------*/

const isContactLeadTopic = (value: string | null): value is ContactLeadTopic => {
    return Boolean(value) && contactLeadTopics.some((topic) => topic === value);
};

const submitContactLead = async (payload: ContactLeadPayload) => {
    const response = await fetch('/leads/contact', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        const errorMessage = (await response.text()).trim();
        throw new Error(errorMessage || 'Unable to send your request right now.');
    }
};

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function ContactLandingPage() {
    useHeader({
        title: 'Contact CrossPath',
        description: 'Contact CrossPath for API access, product demos, partnerships, and hiring support.',
    });

    const [formData, setFormData] = React.useState<ContactFormData>(INITIAL_FORM_DATA);
    const [sourcePage, setSourcePage] = React.useState('');
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState('');

    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const topic = params.get('topic');
        const nextTopic = isContactLeadTopic(topic) ? topic : INITIAL_FORM_DATA.topic;

        setSourcePage(window.location.href);
        setFormData((previous) => ({
            ...previous,
            topic: nextTopic,
            message: previous.message || CONTACT_DEFAULT_MESSAGE_BY_TOPIC[nextTopic],
        }));
    }, []);

    const handleChange = (field: keyof ContactFormData, value: string) => {
        setFormData((previous) => ({
            ...previous,
            [field]: value,
        }));
    };

    const handleTopicChange = (value: ContactLeadTopic) => {
        setFormData((previous) => ({
            ...previous,
            topic: value,
            message: previous.message === CONTACT_DEFAULT_MESSAGE_BY_TOPIC[previous.topic]
                ? CONTACT_DEFAULT_MESSAGE_BY_TOPIC[value]
                : previous.message,
        }));
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSubmitError('');
        setIsSubmitting(true);

        try {
            await submitContactLead({
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: formData.email.trim(),
                phone: formData.phone.trim() || undefined,
                company: formData.company.trim() || undefined,
                topic: formData.topic,
                callbackType: formData.callbackType,
                message: formData.message.trim(),
                sourcePage: sourcePage || undefined,
            });

            setIsSubmitted(true);
            setFormData({
                ...INITIAL_FORM_DATA,
                topic: formData.topic,
                message: CONTACT_DEFAULT_MESSAGE_BY_TOPIC[formData.topic],
            });
        } catch (reason) {
            setSubmitError(reason instanceof Error ? reason.message : 'Unable to send your request right now.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const selectedTopicOption = CONTACT_TOPIC_OPTIONS.find((topicOption) => topicOption.value === formData.topic);

    return (
        <Page withLayout={false}>
            <div className="min-h-screen bg-slate-50 text-slate-950" data-testid="contact-page">
                <PublicHeader ctaLabel="Contact" ctaHref="#contact-form" />

                <main className="mx-auto flex max-w-3xl justify-center px-6 pb-20 pt-28 lg:px-8 lg:pt-32">
                    <section id="contact-form" className="w-full rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/70 sm:p-8">
                        <div className="border-b border-slate-100 pb-6 text-center">
                            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-orange-600">Contact CrossPath</p>
                            <h1 className="mt-3 text-3xl font-bold text-slate-950 sm:text-4xl">Tell us what you need.</h1>
                            <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-600">
                                Use one form for API access, product demos, partnerships, and recruitment support.
                            </p>
                        </div>

                        <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
                            <div>
                                <label className="block text-sm font-semibold text-slate-800" htmlFor="contact-topic">Topic *</label>
                                <select
                                    id="contact-topic"
                                    required
                                    value={formData.topic}
                                    onChange={(event) => {
                                        const nextTopic = event.currentTarget.value;

                                        if (isContactLeadTopic(nextTopic)) {
                                            handleTopicChange(nextTopic);
                                        }
                                    }}
                                    data-testid="contact-topic-select"
                                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                                >
                                    {CONTACT_TOPIC_OPTIONS.map((topicOption) => (
                                        <option key={topicOption.value} value={topicOption.value}>
                                            {topicOption.label}
                                        </option>
                                    ))}
                                </select>
                                {selectedTopicOption ? (
                                    <p className="mt-2 text-xs leading-5 text-slate-500">{selectedTopicOption.description}</p>
                                ) : null}
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-800">First name *</label>
                                    <input
                                        required
                                        value={formData.firstName}
                                        onChange={(event) => handleChange('firstName', event.currentTarget.value)}
                                        className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                                        placeholder="John"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-800">Last name *</label>
                                    <input
                                        required
                                        value={formData.lastName}
                                        onChange={(event) => handleChange('lastName', event.currentTarget.value)}
                                        className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                                        placeholder="Doe"
                                    />
                                </div>
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-800">Work email *</label>
                                    <input
                                        required
                                        type="email"
                                        value={formData.email}
                                        onChange={(event) => handleChange('email', event.currentTarget.value)}
                                        className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                                        placeholder="john@company.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-800">Phone / WhatsApp</label>
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(event) => handleChange('phone', event.currentTarget.value)}
                                        className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                                        placeholder="+33 6 00 00 00 00"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-800">Company</label>
                                <input
                                    value={formData.company}
                                    onChange={(event) => handleChange('company', event.currentTarget.value)}
                                    className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                                    placeholder="Company name"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-800">Preferred reply *</label>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {CONTACT_CALLBACK_OPTIONS.map((callbackOption) => (
                                        <button
                                            key={callbackOption.value}
                                            type="button"
                                            onClick={() => handleChange('callbackType', callbackOption.value)}
                                            data-testid={`contact-callback-${callbackOption.value}`}
                                            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                                                formData.callbackType === callbackOption.value
                                                    ? 'bg-slate-950 text-white'
                                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                            }`}
                                        >
                                            {callbackOption.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-800">Message *</label>
                                <textarea
                                    required
                                    rows={5}
                                    value={formData.message}
                                    onChange={(event) => handleChange('message', event.currentTarget.value)}
                                    className="mt-2 w-full resize-y rounded-2xl border border-slate-200 px-4 py-3 text-sm leading-6 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                                    placeholder="Share what you need, the API or product area, timing, and any integration context."
                                />
                            </div>

                            {isSubmitted ? (
                                <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-700">
                                    <CheckCircle2 size={18} />
                                    Request sent. We will contact you within 2 business hours.
                                </div>
                            ) : submitError ? (
                                <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
                                    {submitError}
                                </div>
                            ) : null}

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-pink-500 px-6 py-4 text-sm font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:from-orange-600 hover:to-pink-600 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {isSubmitting ? 'Sending...' : 'Send request'}
                                <ArrowRight size={18} />
                            </button>

                        </form>
                    </section>
                </main>
            </div>
        </Page>
    );
}
